/**
 * A `?<param>=` deep link to ONE item of a list page — how the Signal Inbox points at the source connector or the
 * dead-letter queue a signal is about (`/sources?connector=`, `/queues?queue=`).
 *
 * Pure and vitest-safe. The pages read the parameter and mark the item; a link naming an item the page does not
 * list says so, rather than silently showing the whole list as if the link had narrowed it.
 */

/** The linked key, trimmed; null when the parameter is absent or blank. */
export function linkedKey(search: URLSearchParams, param: string): string | null {
  const raw = search.get(param)?.trim()
  return raw ? raw : null
}

/**
 * Whether `candidate` is the linked item. Case is ignored, as the signal store keys its subjects
 * (`OperationalSignalRules.KeyComparer`, OrdinalIgnoreCase) — the source rule lowercases connector ids.
 */
export function isLinked(candidate: string, linked: string | null): boolean {
  return linked !== null && candidate.trim().toUpperCase() === linked.toUpperCase()
}

/**
 * The store replaces a subject key longer than its column with `sha256:<hex>` (`OperationalSignalRules.ToSubjectKey`),
 * which names nothing a page lists, so such a key is never offered as a deep link.
 */
export function isLinkableSubjectKey(key: string | null | undefined): key is string {
  const trimmed = key?.trim()
  return Boolean(trimmed) && !trimmed!.startsWith('sha256:')
}
